import React, { useRef, useState, useEffect } from 'react'
import TeamHeader from '../TeamHeader/TeamHeader'
import Chart from 'react-apexcharts'
import type { ApexOptions } from 'apexcharts'
import './ActivityAndPerformance.css'
import LoadingSpinner from '../../backend/components/LoadingSpinner';
import { createLightningDivIcon, formatRegionName, formatTimestamp } from "../TeamPortalUtil/regionFormatter";

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    ScaleControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap } from "leaflet";

// Icons
import { IoOptionsOutline } from "react-icons/io5";
import { CiCircleCheck } from "react-icons/ci";
import { SlTarget } from "react-icons/sl";
import { IoMdArrowDropdown } from "react-icons/io";
import { FaCircleInfo } from "react-icons/fa6";

interface RawLightning {
    idx: number;
    dttime_utc: string;
    ltg_lat: number;
    ltg_lon: number;
    nsta: number;
    polarity: number; // <-- used for distribution
    type: number;
    stamac: string;
    geometry: any | null;
    modified: boolean;
    lcc: number;
    lcc_durs: string | null;
    lcc_mac: string | null;
    lcc_sta: string | null;
    lcc_stamacs: string | null;
    lcc_ts: string | null;
    q_lcc: number | null;
}

interface PolarityBin {
    label: string;
    min?: number;
    max?: number;
    count: number;
    fullLabel?: string; // <-- add this
}

interface LightningRaw {
    nsta: number;
}

export interface ToaEfficiencyData {
    entry_id: number;
    total_lightning_events: number;
    total_lightning_events_detected: number;
    location_accuracy: number;
    region: string;
    timestamp: string;
}

interface MonthlyEfficiency {
    year: number;
    month: number; // 0-11
    total_lightning_events: number;
    total_lightning_events_detected: number;
    location_accuracy: number;
    count: number; // number of entries
}

// Region options
interface ToaRegions {
    region_name: string;
}

type TimeframeOption = {
    label: string;
    hours: number;
};

function getLightningIcon(d: RawLightning, maxHours: number): Icon {
    const eventTime = new Date(d.dttime_utc).getTime();
    const now = Date.now();
    const hoursAgo = (now - eventTime) / (1000 * 60 * 60);

    return createLightningDivIcon(
        d.polarity,
        hoursAgo,
        maxHours
    ) as unknown as Icon; // ✅ cast DivIcon to Icon
}

function useClickOutside(ref: React.RefObject<HTMLElement>, handler: () => void) {
    useEffect(() => {
        const listener = (event: MouseEvent | TouchEvent) => {
            if (!ref.current || ref.current.contains(event.target as Node)) {
                return;
            }
            handler();
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
}


const ActivityAndPerformance = () => {

    const ChartSkeleton = () => (
        <div className="toa-chart-skeleton skeleton " />
    );

    const StatSkeleton = () => (
        <div className="toa-info-item skeleton" />
    );

    const MapSkeleton = () => (
        <div className="toa-map-skeleton">
            <LoadingSpinner />
        </div>
    );

    function showInfoTooltip(message: string, extraClassName?: string) {
        return (
            <div className="tooltip-wrapper">
                <FaCircleInfo />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {message}
                </div>
            </div>
        );
    }

    /* ----------------------------------------------------
        DROPDOWN LOGIC
    ---------------------------------------------------- */

    const TIMEFRAMES: TimeframeOption[] = [
        { label: "2 hours", hours: 2 },
        { label: "8 hours", hours: 8 },
        { label: "1 day", hours: 24 },
        { label: "7 days", hours: 168 },
    ];

    const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>(TIMEFRAMES[1]); // default 8 hours

    const [pipelineFilter, setPipelineFilter] = useState<{
        region_name?: string;
    }>({
        region_name: "florida",
    });

    interface DropdownProps {
        title: string;
        options: string[];
        selected?: string;
        onSelect: (value: string) => void;
        open: boolean;
        setOpen: (val: boolean) => void;
    }

    const Dropdown: React.FC<DropdownProps> = ({ title, options, selected, onSelect, open, setOpen }) => {
        return (
            <div className="general-dropdown">
                <div className="general-dropdown-header" onClick={() => setOpen(!open)}>
                    <span>{title}</span>
                    <IoMdArrowDropdown className={`general-dropdown-arrow ${open ? "open" : ""}`} />
                </div>
                <div className={`general-dropdown-options ${open ? "open" : ""}`}>
                    {options.map((o) => (
                        <div
                            key={o}
                            className={`general-dropdown-option ${selected === o ? "active" : ""}`}
                            onClick={() => onSelect(o)}
                        >
                            {formatRegionName(o)} {/* ✅ Format the display */}
                        </div>
                    ))}
                </div>

            </div>
        );
    };

    const [pipelineDropdownOpen, setPipelineDropdownOpen] = useState<{
        region_name: boolean;
    }>({
        region_name: false,
    });

    const toggleFilter = (
        setStateFn: React.Dispatch<React.SetStateAction<any>>,
        key: string,
        value: string
    ) => {
        setStateFn((prev: any) => ({
            ...prev,
            [key]: prev[key] === value ? undefined : value,
        }));
    };

    // Adding functionality to close dropdown whenever the user clicks outside its component
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as HTMLElement;

            // If click is inside any dropdown area, do nothing
            if (
                target.closest(".general-sort-options") ||
                target.closest(".general-sort-dropdown") ||
                target.closest(".general-dropdown") ||
                target.closest(".general-dropdown-options")
            ) {
                return;
            }

            // Close ALL dropdowns
            setPipelineDropdownOpen({ region_name: false });

            // Also close <details> elements (Filter / Sort)
            document.querySelectorAll(".general-sort-dropdown").forEach((el) => {
                (el as HTMLDetailsElement).open = false;
            });
        }

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    /* ----------------------------------------------------
        Region Information
    ---------------------------------------------------- */
    const [toaRegions, setToaRegions] = useState<ToaRegions[]>([]);

    const fetchToaRegions = async () => {
        try {
            const res = await fetch("/portal/api/regions_info/", {
                credentials: "same-origin",
            });
            if (!res.ok) throw new Error(`Pipeline HTTP ${res.status}`);
            setToaRegions(await res.json());
        } catch (err) {
            console.error("Error fetching Prefect Pipeline History:", err);
            setToaRegions([]);
        }
    };

    useEffect(() => {
        fetchToaRegions();
    }, []);

    //  useEffect(() => {
    //     console.log("🔧 Regions:", toaRegions);
    // }, [toaRegions]);

    /* ----------------------------------------------------
        Lightning
    ---------------------------------------------------- */
    const [timeRange, setTimeRange] = useState<"12m" | "1m" | "1w" | "24h">("12m");

    // Lightning Variables
    const [lightningData, setLightningData] = useState<RawLightning[]>([]);
    const [latestEventTime, setLatestEventTime] = useState<string>("—");
    const selectedRegion = pipelineFilter.region_name || "florida"; // fallback
    const [lightningLoading, setLightningLoading] = useState<boolean>(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);

    useEffect(() => {
        if (!selectedRegion || !selectedTimeframe) return;

        const controller = new AbortController();
        setLightningLoading(true);

        const fetchLightning = async () => {
            try {
                const res = await fetch(
                    `/portal/api/lightning/raw/?region=${encodeURIComponent(
                        selectedRegion
                    )}&hours=${selectedTimeframe.hours}`,
                    {
                        signal: controller.signal,
                        credentials: "same-origin",
                    }
                );

                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const text = await res.text();
                const fixedText = text.replace(/\bNaN\b/g, "null");
                const data: RawLightning[] = JSON.parse(fixedText);
                setLightningData(data);
                setHasLoadedOnce(true);

                if (data.length > 0) {
                    const latest = data
                        .map(d => new Date(d.dttime_utc))
                        .sort((a, b) => b.getTime() - a.getTime())[0];

                    setLatestEventTime(
                        latest.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZoneName: "short",
                        })
                    );
                } else {
                    setLatestEventTime("—");
                }
            } catch (err) {
                if ((err as any).name !== "AbortError") {
                    console.error("Lightning fetch error:", err);

                    // ✅ Clear previous data so charts show "No data"
                    setLightningData([]);
                    setLatestEventTime("—");
                    setHasLoadedOnce(true); // ensures skeleton hides
                }
            } finally {
                setLightningLoading(false);
            }
        };

        fetchLightning();
        return () => controller.abort();
    }, [selectedRegion, selectedTimeframe]);


    /* ----------------------------------------------------
        Lightning Activity Chart
    ---------------------------------------------------- */
    function buildNstaDistribution(data: LightningRaw[]) {
        const bins: Record<string, number> = {
            "4": 0,
            "5": 0,
            "6": 0,
            "7": 0,
            "8": 0,
            "9+": 0
        };

        data.forEach(d => {
            if (d.nsta >= 9) bins["9+"]++;
            else bins[d.nsta.toString()]++;
        });

        return {
            categories: Object.keys(bins),
            series: Object.values(bins)
        };
    }

    const { categories, series } = buildNstaDistribution(lightningData);

    const lightningActivitySeries = [
        {
            name: "Lightning Events",
            data: series
        }
    ];


    const lightningActivityOptions: ApexOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false }
        },
        plotOptions: {
            bar: {
                columnWidth: "65%",
                borderRadius: 4,
                distributed: true
            }
        },
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories,
            title: {
                text: "Stations Reporting (nsta)",
                style: { fontSize: "12px", fontWeight: 500 }
            },
            labels: {
                style: { fontSize: "11px" }
            }
        },
        tooltip: {
            y: {
                formatter: v => `${v.toLocaleString()} strikes`
            }
        },
        colors: [
            "#a7b8cdff", // 4
            "#7b8ca4ff", // 5
            "#5dd3d8", // 6
            "#22b4c6", // 7
            "#0b5563", // 8
            "#083344"  // 9+
        ],
        grid: {
            yaxis: { lines: { show: true } }
        },
        legend: { show: false }
    };

    /* ----------------------------------------------------
        Current Distribution Chart
    ---------------------------------------------------- */

    const POLARITY_BINS: PolarityBin[] = [
        { label: "< -100", min: undefined, max: -100_000, count: 0, fullLabel: "< -100k" },
        { label: "-100 to -50", min: -100_000, max: -50_000, count: 0, fullLabel: "-100k to -50k" },
        { label: "-50 to 0", min: -50_000, max: 0, count: 0, fullLabel: "-50k to 0" },
        { label: "0 to 50", min: 0, max: 50_000, count: 0, fullLabel: "0–50k" },
        { label: "> 50", min: 50_000, max: undefined, count: 0, fullLabel: "> 50k" }
    ];

    function buildPolarityHistogram(data: RawLightning[]) {
        // Reset counts
        POLARITY_BINS.forEach(b => (b.count = 0));

        data.forEach(d => {
            const val = d.polarity;
            if (typeof val !== "number") return;

            const bin = POLARITY_BINS.find(b =>
                (b.min === undefined || val >= b.min) &&
                (b.max === undefined || val < b.max)
            );

            if (bin) bin.count++;
        });

        return {
            labels: POLARITY_BINS.map(b => b.label), // Short labels for x-axis
            counts: POLARITY_BINS.map(b => b.count),
            fullLabels: POLARITY_BINS.map(b => b.fullLabel) // For tooltip
        };
    }

    const { labels: polarityCategories, counts: polarityValues, fullLabels: polarityTooltips } =
        buildPolarityHistogram(lightningData);

    const currentDistributionSeries = [
        { name: "Count", data: polarityValues }
    ];

    const currentDistributionOptions: ApexOptions = {
        chart: {
            type: "bar",
            toolbar: { show: false }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "70%",
                borderRadius: 4,
                distributed: true
            }
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: polarityCategories,
            title: {
                text: "Peak Current Range (kA)",
                style: { fontSize: "12px", fontWeight: 500 }
            },
            labels: { style: { fontSize: "11px" }, rotate: -30 },
            axisBorder: { show: true },
            axisTicks: { show: true }
        },
        colors: ["#5dd3d8", "#0b5563", "#22b4c6", "#7a8a9e", "#1fc9d6"],
        grid: {
            borderColor: "#e5e7eb",
            strokeDashArray: 0,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } }
        },
        legend: { show: false },
        tooltip: {
            enabled: true,
            y: {
                formatter: (val: number, opts) => {
                    // Show the full range in the tooltip
                    const idx = opts.dataPointIndex;
                    return `${val.toLocaleString()} (${polarityTooltips[idx]})`;
                }
            }
        }
    };

    /* ----------------------------------------------------
        EFFICIENCY YEARLY GRAPH LOGIC
    ---------------------------------------------------- */

    const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

    function aggregateEfficiencyByMonth(data: ToaEfficiencyData[]) {
        const map = new Map<string, MonthlyEfficiency>();

        data.forEach(d => {
            const date = new Date(d.timestamp);
            const key = `${date.getFullYear()}-${date.getMonth()}`;

            if (!map.has(key)) {
                map.set(key, {
                    year: date.getFullYear(),
                    month: date.getMonth(),
                    total_lightning_events: 0,
                    total_lightning_events_detected: 0,
                    location_accuracy: 0,
                    count: 0
                });
            }

            const entry = map.get(key)!;
            entry.total_lightning_events += d.total_lightning_events;
            entry.total_lightning_events_detected += d.total_lightning_events_detected;
            entry.location_accuracy += d.location_accuracy;
            entry.count += 1;
        });

        // Compute averages
        return Array.from(map.values())
            .map(e => ({
                year: e.year,
                month: e.month,
                total_lightning_events: e.total_lightning_events / e.count,
                total_lightning_events_detected: e.total_lightning_events_detected / e.count,
                location_accuracy: Number(
                    (e.location_accuracy / e.count).toFixed(2)
                ),
            }))
            .sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month));
    }

    function Tooltip({
        message,
        showTooltip,
        onClose,
        children,
        extraClassName = "",
    }: {
        message: React.ReactNode;
        showTooltip: boolean;
        onClose?: () => void;
        children: React.ReactNode;
        extraClassName?: string;
    }) {
        const tooltipRef = useRef<HTMLDivElement>(null);

        useClickOutside(tooltipRef, () => {
            if (showTooltip && onClose) {
                onClose();
            }
        });

        return (
            <div className="tooltip-button-wrapper-click" ref={tooltipRef}>
                {children}
                {showTooltip && (
                    <div
                        className={`tooltip-text-click show ${extraClassName}`}
                        onClick={(e) => e.stopPropagation()} // Prevent clicks inside from bubbling
                    >
                        {message}
                    </div>
                )}
            </div>
        );
    }



    /* ----------------------------------------------------
        Location Efficiency Chart
    ---------------------------------------------------- */
    const [toaEfficiencyHistory, setToaEfficiency] = useState<ToaEfficiencyData[]>([]);
    const [loadingEfficiency, setLoadingEfficiency] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    // Selected region (null = All regions)
    const [selectedEfficiencyRegion, setSelectedEfficiencyRegion] =
        useState<string | "ALL">("ALL");

    // Unique regions available from efficiency data
    const efficiencyRegions = React.useMemo(() => {
        return Array.from(new Set(toaEfficiencyHistory.map(d => d.region)));
    }, [toaEfficiencyHistory]);

    // Region-filtered efficiency data
    const regionFilteredEfficiency = React.useMemo(() => {
        if (selectedEfficiencyRegion === "ALL") return toaEfficiencyHistory;
        return toaEfficiencyHistory.filter(
            d => d.region === selectedEfficiencyRegion
        );
    }, [toaEfficiencyHistory, selectedEfficiencyRegion]);


    const fetchToaEfficiency = async () => {
        setLoadingEfficiency(true);
        try {
            const res = await fetch("/api/toateam/efficiency/history/", {
                credentials: "same-origin",
            });
            if (!res.ok) throw new Error(`Pipeline HTTP ${res.status}`);
            setToaEfficiency(await res.json());
        } catch (err) {
            console.error("Error fetching Prefect Pipeline History:", err);
            setToaEfficiency([]);
        } finally {
            setLoadingEfficiency(false);
        }
    };

    useEffect(() => {
        fetchToaEfficiency();
    }, []);

    function filterEfficiencyByTimeRange(
        data: ToaEfficiencyData[],
        range: "12m" | "1m" | "1w" | "24h",
        month?: number,
        year?: number
    ) {
        const now = new Date();
        const cutoff = new Date();

        switch (range) {
            case "12m":
                cutoff.setMonth(now.getMonth() - 12);
                return data.filter(d => new Date(d.timestamp) >= cutoff);
            case "1m":
                if (month !== undefined && year !== undefined) {
                    return data.filter(d => {
                        const date = new Date(d.timestamp);
                        return date.getMonth() === month && date.getFullYear() === year;
                    });
                }
                // fallback: last 30 days
                cutoff.setDate(now.getDate() - 30);
                return data.filter(d => new Date(d.timestamp) >= cutoff);
            case "1w":
                cutoff.setDate(now.getDate() - 7);
                return data.filter(d => new Date(d.timestamp) >= cutoff);
            case "24h":
                cutoff.setDate(now.getDate() - 1);
                return data.filter(d => new Date(d.timestamp) >= cutoff);
        }
    }


    // Filter based on selected time range
    const filteredEfficiency = React.useMemo(() => {
        const data = filterEfficiencyByTimeRange(
            regionFilteredEfficiency,
            timeRange,
            selectedMonth,
            selectedYear
        ).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        if (timeRange === "12m") {
            return aggregateEfficiencyByMonth(data);
        }

        return data;
    }, [regionFilteredEfficiency, timeRange, selectedMonth, selectedYear]);


    const efficiencyCategories = filteredEfficiency.map(d => {
        if ('year' in d && 'month' in d) {
            // MonthlyEfficiency
            return new Date(d.year, d.month).toLocaleDateString("en-US", {
                month: "short",
                // year: "2-digit"
            });
        } else {
            // ToaEfficiencyData
            return new Date(d.timestamp).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric"
            });
        }
    });

    const isYearly = timeRange === "12m";

    const locationEfficiencySeries = [
        {
            name: isYearly ? "Avg. Lightning Events" : "Total Lightning Events",
            type: "column",
            data: filteredEfficiency.map(d => d.total_lightning_events),
        },
        {
            name: isYearly ? "Avg. Lightning Events Detected" : "Total Lightning Events Detected",
            type: "column",
            data: filteredEfficiency.map(d => d.total_lightning_events_detected),
        },
        {
            name: isYearly ? "Avg. Location Accuracy" : "Location Accuracy",
            type: "area",
            data: filteredEfficiency.map(d => d.location_accuracy),
        },
    ];

    const locationEfficiencyOptions: ApexOptions = {
        chart: {
            stacked: false,
            toolbar: { show: false },
        },
        stroke: {
            width: [0, 0, 2],
            curve: "smooth",
        },
        plotOptions: {
            bar: {
                columnWidth: "45%",
                borderRadius: 5,
            },
        },
        fill: {
            type: ["solid", "solid", "gradient"],
            gradient: {
                type: "vertical",
                shadeIntensity: 0,
                opacityFrom: 0.35,
                opacityTo: 0.05,
            },
        },
        colors: ["#1f6fd2", "#18c3f2", "#4da3ff"],
        dataLabels: {
            enabled: false,
        },
        legend: {
            position: "top",
            horizontalAlign: "center",
            fontSize: "12px",
        },
        xaxis: {
            categories: efficiencyCategories,
            labels: {
                style: { fontSize: "11px" },
            },
        },
        yaxis: [
            {
                // LEFT — event counts (for BOTH column series)
                seriesName: [
                    isYearly ? "Avg. Lightning Events" : "Total Lightning Events",
                    isYearly ? "Avg. Lightning Events Detected" : "Total Lightning Events Detected"
                ],
                title: {
                    text: "Lightning Events",
                },
                min: 0,
                labels: {
                    formatter: val => Math.round(val).toLocaleString(),
                },
            },
            {
                // RIGHT — accuracy %
                seriesName: isYearly ? "Avg. Location Accuracy" : "Location Accuracy",
                opposite: true,
                min: 0,
                max: 100,
                tickAmount: 5,
                title: {
                    text: "Location Accuracy (%)",
                },
                labels: {
                    formatter: val => `${val}%`,
                },
            },
        ],
        tooltip: {
            shared: true,
            intersect: false,
            y: {
                formatter: (val, { seriesIndex }) =>
                    seriesIndex === 2 ? `${val}%` : val.toLocaleString(),
            },
        },
        grid: {
            borderColor: "#e5e7eb",
            strokeDashArray: 4,
        },
    };

    const needsScroll =
        filteredEfficiency.length > 12 &&
        (screenWidth < 1000 || screenWidth > 1350);

    const chartWidth = needsScroll
        ? `${filteredEfficiency.length * 65}px`
        : "100%";


    /* ----------------------------------------------------
    Network Performance Calculations
    ---------------------------------------------------- */

    // Compute past 14 days
    const now = new Date();
    const cutoff14Days = new Date();
    cutoff14Days.setDate(now.getDate() - 14);

    // Last 14 days + region filter
    const last14DaysData = regionFilteredEfficiency.filter(
        d => new Date(d.timestamp) >= cutoff14Days
    );

    // Detection Efficiency
    const avgDetectionEfficiency =
        last14DaysData.length > 0
            ? last14DaysData.reduce(
                (sum, d) =>
                    sum +
                    (d.total_lightning_events_detected /
                        (d.total_lightning_events || 1)) *
                    100,
                0
            ) / last14DaysData.length
            : 0;

    // Location Accuracy
    const avgLocationAccuracy =
        last14DaysData.length > 0
            ? last14DaysData.reduce(
                (sum, d) => sum + d.location_accuracy,
                0
            ) / last14DaysData.length
            : 0;



    /* ----------------------------------------------------
        Estimate most active zone
    ---------------------------------------------------- */

    function getMostActiveZone(data: RawLightning[]) {
        if (data.length === 0) return null;

        // Round lat/lon to 1 decimal for coarse clustering
        const counts: Record<string, number> = {};
        data.forEach(d => {
            const key = `${d.ltg_lat.toFixed(1)},${d.ltg_lon.toFixed(1)}`;
            counts[key] = (counts[key] || 0) + 1;
        });

        // Find the key with max count
        let maxCount = 0;
        let maxKey: string | null = null;
        for (const key in counts) {
            if (counts[key] > maxCount) {
                maxCount = counts[key];
                maxKey = key;
            }
        }

        return maxKey; // e.g., "29.9,-86.4"
    }

    // ---------------------------
    // React hook for fetching zone name
    // ---------------------------
    function useMostActiveZoneName(lightningData: RawLightning[]) {
        const [mostActiveZoneName, setMostActiveZoneName] = useState<string>("—");
        const [loading, setLoading] = useState<boolean>(false);

        const mostActiveZoneKey = getMostActiveZone(lightningData);

        useEffect(() => {
            async function fetchZoneName(lat: number, lon: number) {
                setLoading(true);
                try {
                    const res = await fetch(
                        `/portal/api/reverse-geocode/?lat=${lat}&lon=${lon}`,
                        { credentials: "same-origin" }
                    );

                    if (!res.ok) throw new Error();
                    const data = await res.json();
                    setMostActiveZoneName(data.name ?? "—");
                } catch {
                    setMostActiveZoneName("—");
                } finally {
                    setLoading(false);
                }
            }

            if (mostActiveZoneKey) {
                const [lat, lon] = mostActiveZoneKey.split(",").map(Number);
                if (!isNaN(lat) && !isNaN(lon)) {
                    fetchZoneName(lat, lon);
                }
            }
        }, [mostActiveZoneKey]);


        return { mostActiveZoneName, loading };
    }

    const { mostActiveZoneName, loading: mostActiveZoneLoading } = useMostActiveZoneName(lightningData);

    /* ----------------------------------------------------
        MAP
    ---------------------------------------------------- */
    const mapRef = useRef<LeafletMap | null>(null);

    // Compute map center and zoom from lightning data
    const mapCenter: [number, number] = lightningData.length > 0
        ? [
            lightningData.reduce((sum, d) => sum + d.ltg_lat, 0) / lightningData.length,
            lightningData.reduce((sum, d) => sum + d.ltg_lon, 0) / lightningData.length,
        ]
        : [0, 0];

    // Optional: set a default zoom depending on spread
    const mapZoom = lightningData.length > 0 ? 6 : 2; // Adjust 6 if needed for FL area

    /* ----------------------------------------------------
        MARKERS
    ---------------------------------------------------- */
    const lightningMarkers = lightningData.map(d => {
        const icon = getLightningIcon(d, selectedTimeframe.hours);

        return (
            <Marker
                key={d.idx}
                position={[d.ltg_lat, d.ltg_lon]}
                icon={icon}
            >
                <Popup>
                    <div style={{ marginTop: '1rem', marginBottom: '1rem', minWidth: "180px" }}>
                        <br /><strong>Date:</strong> {formatTimestamp(d.dttime_utc)}
                        <br /><strong>Latitude:</strong> {d.ltg_lat.toFixed(4)}
                        <br /><strong>Longitude:</strong> {d.ltg_lon.toFixed(4)}
                        <br /><strong>LCC:</strong> {d.lcc}
                        <br /><strong>Peak Current:</strong> {d.polarity}
                        <br /><strong>Nsta:</strong> {d.nsta}
                        <br /><strong>Type:</strong> {d.type === 0 ? "CG" : "IG"}
                    </div>
                </Popup>
            </Marker>
        );
    });



    return (
        <div className="customer-directory-body toa-body">
            <TeamHeader title="TOA" />

            <div className="customer-title-and-expand-buttons">
                <h1 className="cutomer-title">Activity and Performance</h1>
            </div>

            <div className='toa-dash-container'>

                {/* Map Block */}
                <div className="toa-dash-block">
                    {!hasLoadedOnce || lightningLoading ? (
                        <MapSkeleton />
                    ) : (
                        <div className="toa-map-wrapper">
                            {/* Overlay buttons */}
                            <div className="ai-expand-buttons toa-in-map-buttons">
                                <details className="general-sort-dropdown">
                                    <summary className="general-sort-summary">
                                        <IoOptionsOutline style={{ marginRight: "0.25rem", fontSize: "1rem" }} />
                                        <span>Filter</span>
                                    </summary>

                                    <div className="general-sort-options">
                                        <Dropdown
                                            title="Area"
                                            options={Array.from(new Set(toaRegions.map(r => r.region_name)))}
                                            selected={pipelineFilter.region_name}
                                            onSelect={(region) =>
                                                toggleFilter(setPipelineFilter, "region_name", region)
                                            }
                                            open={pipelineDropdownOpen.region_name}
                                            setOpen={(val) =>
                                                setPipelineDropdownOpen(prev => ({
                                                    ...prev,
                                                    region_name: val,
                                                }))
                                            }
                                        />

                                        <button onClick={() => setPipelineFilter({})}>
                                            Reset
                                        </button>
                                    </div>
                                </details>

                                <select
                                    className="general-sort-summary toa-select"
                                    value={selectedTimeframe.hours}
                                    onChange={(e) => {
                                        const tf = TIMEFRAMES.find(t => t.hours === Number(e.target.value));
                                        if (tf) setSelectedTimeframe(tf);
                                    }}
                                >
                                    {TIMEFRAMES.map(tf => (
                                        <option key={tf.hours} value={tf.hours}>
                                            {tf.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Leaflet map */}
                            <MapContainer
                                center={mapCenter}
                                zoom={mapZoom}
                                minZoom={2}
                                style={{ width: "100%", height: "100%" }}
                                ref={mapRef}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution="&copy; OpenStreetMap"
                                />

                                {/* ✅ Scale control */}
                                <ScaleControl
                                    position="bottomleft"
                                    metric={true}
                                    imperial={false}
                                    maxWidth={200}
                                />

                                {lightningMarkers}
                            </MapContainer>
                        </div>
                    )}
                </div>



                {/* Lightning Block  */}
                <div className='toa-dash-block toa-lightning-block'>
                    <div className='toa-info'>
                        {!hasLoadedOnce || lightningLoading ? (
                            <>
                                <StatSkeleton />
                                <StatSkeleton />
                                <StatSkeleton />
                            </>
                        ) : (
                            <>
                                {/* Total Lightning always shown */}
                                <div className='toa-info-item'>
                                    <p>Total Lightning</p>
                                    <strong>{lightningData.length.toLocaleString()}</strong>
                                </div>

                                {/* Most Active Zone */}
                                {mostActiveZoneLoading ? (
                                    <StatSkeleton />
                                ) : (
                                    <div className='toa-info-item'>
                                        <p>Most Active Zone</p>
                                        <strong>{mostActiveZoneName || "—"}</strong>
                                    </div>
                                )}

                                {/* Latest Event Time always shown */}
                                <div className='toa-info-item'>
                                    <p>Latest Event Time</p>
                                    <strong>{latestEventTime}</strong>
                                </div>
                            </>
                        )}
                    </div>


                    <div className='toa-charts'>
                        <div className='toa-chart'>
                            <h1 className='toa-dash-block-performance-title'>Lightning Activity</h1>
                            <div className='toa-chart-wrapper'>
                                {!hasLoadedOnce || lightningLoading ? (
                                    <ChartSkeleton />
                                ) : series.some(v => v > 0) ? (
                                    <Chart
                                        options={lightningActivityOptions}
                                        series={lightningActivitySeries}
                                        type="bar"
                                        height="100%"
                                    />
                                ) : (
                                    <div className="no-data-message">
                                        No data for the selected region and time period.
                                    </div>
                                )}
                            </div>
                        </div>


                        <div className='toa-chart'>
                            <h1 className='toa-dash-block-performance-title'>Current Distribution</h1>
                            <div className='toa-chart-wrapper'>
                                {!hasLoadedOnce || lightningLoading ? (
                                    <ChartSkeleton />
                                ) : polarityValues.some(v => v > 0) ? (
                                    <Chart
                                        options={currentDistributionOptions}
                                        series={currentDistributionSeries}
                                        type="bar"
                                        height="100%"
                                    />
                                ) : (
                                    <div className="no-data-message">
                                        No data for the selected region and time period.
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Network Block */}
                <div className='toa-dash-block'>
                    <div className='toa-dash-block-performance'>
                        <h1 className='toa-dash-block-performance-title toa-title-alone'>
                            Network Performance
                        </h1>

                        <p className='toa-performance-text'>
                            Average of last 2 weeks
                            {selectedEfficiencyRegion !== "ALL" && (
                                <> — Region: <strong>{selectedEfficiencyRegion}</strong></>
                            )}
                        </p>

                        <div className='toa-gap'></div>

                        {loadingEfficiency ? (
                            <>
                                <StatSkeleton />
                                <StatSkeleton />
                            </>
                        ) : (
                            <>
                                {/* DETECTION EFFICIENCY */}
                                <div className="server-stat-card toa-card">
                                    <div className="server-stat">
                                        <CiCircleCheck className="server-stat-icon toa-stat-icon-ext" />
                                        <div>
                                            <strong>
                                                Avg. Detection Efficiency {showInfoTooltip("Calculated daily as (detected lightning ÷ total lightning events) × 100.")}
                                            </strong>
                                            <p>Goal: 100%</p>
                                        </div>
                                    </div>

                                    <div className="server-stat2">
                                        <p>{avgDetectionEfficiency.toFixed(1)}%</p>

                                        <div className="stat-bar">
                                            <div
                                                className="stat-bar-fill"
                                                style={{ width: `${avgDetectionEfficiency}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>

                                {/* LOCATION ACCURACY */}
                                <div className="server-stat-card toa-card">
                                    <div className="server-stat">
                                        <SlTarget className="server-stat-icon toa-stat-icon-ext" />
                                        <div>
                                            <strong>
                                                Avg. Location Accuracy {showInfoTooltip("Daily percentage of detected lightning events whose location error is ≤ 1 km.")}
                                            </strong>
                                            <p>Goal: 100%</p>
                                        </div>
                                    </div>

                                    <div className="server-stat2">
                                        <p>{avgLocationAccuracy.toFixed(1)}%</p>

                                        <div className="stat-bar">
                                            <div
                                                className="stat-bar-fill load"
                                                style={{ width: `${avgLocationAccuracy}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}



                    </div>
                </div>

                {/* Location Efficiency Block */}
                <div className='toa-dash-block'>
                    <div className="request-title-and-times toa-location">
                        <h1 className='toa-dash-block-performance-title'>Location Efficiency</h1>

                        <div className='toa-eff-filters'>
                            <select
                                className="general-sort-summary toa-select2"
                                value={selectedEfficiencyRegion}
                                onChange={(e) =>
                                    setSelectedEfficiencyRegion(e.target.value as any)
                                }
                            >
                                <option value="ALL">All Regions</option>
                                {efficiencyRegions.map(region => (
                                    <option key={region} value={region}>
                                        {region}
                                    </option>
                                ))}
                            </select>


                            <div className="request-times">
                                <button
                                    className={timeRange === "12m" ? "active" : ""}
                                    onClick={() => setTimeRange("12m")}
                                >
                                    12 months
                                </button>

                                <Tooltip
                                    showTooltip={showMonthYearPicker}
                                    onClose={() => setShowMonthYearPicker(false)}
                                    message={
                                        <div className="month-year-selector">
                                            <select
                                                value={selectedMonth}
                                                onChange={e => {
                                                    setSelectedMonth(Number(e.target.value));
                                                }}
                                            >
                                                {[...Array(12)].map((_, i) => (
                                                    <option key={i} value={i}>
                                                        {new Date(0, i).toLocaleString('en-US', { month: 'long' })}
                                                    </option>
                                                ))}
                                            </select>
                                            <select
                                                value={selectedYear}
                                                onChange={e => {
                                                    setSelectedYear(Number(e.target.value));
                                                }}
                                            >
                                                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                                                    <option key={y} value={y}>{y}</option>
                                                ))}
                                            </select>
                                        </div>
                                    }
                                >
                                    <button
                                        className={timeRange === "1m" ? "active" : ""}
                                        style={{ borderRight: "1px solid #ddd" }}
                                        onClick={() => {
                                            setTimeRange("1m");
                                            setShowMonthYearPicker(true); // Show tooltip when switching to 1m
                                        }}
                                    >
                                        1 month
                                    </button>
                                </Tooltip>

                                <button
                                    className={timeRange === "1w" ? "active" : ""}
                                    onClick={() => setTimeRange("1w")}
                                >
                                    1 week
                                </button>
                                <button
                                    className={timeRange === "24h" ? "active" : ""}
                                    onClick={() => setTimeRange("24h")}
                                >
                                    24 hours
                                </button>
                            </div>


                        </div>
                    </div>

                    {/* Location Efficiency */}
                    <div className="toa-chart efficiency-legend-fix">
                        {loadingEfficiency ? (
                            <ChartSkeleton />
                        ) : filteredEfficiency.length > 0 ? (
                            <div className="efficiency-chart-scroll-wrapper">
                                <div
                                    className="efficiency-chart-inner"
                                    style={{ width: chartWidth }}
                                >
                                    <Chart
                                        options={locationEfficiencyOptions}
                                        series={locationEfficiencySeries}
                                        type="line"
                                        height="100%"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="no-data-message">
                                No data for the selected time period.
                            </div>
                        )}
                    </div>




                </div>

            </div>
        </div>
    )
}

export default ActivityAndPerformance